import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "@tanstack/react-router";
import { useToast } from "@perlab/ui";
import { NotificationPrefsPanel } from "../components/NotificationPrefsPanel";
import { navTo } from "../components/navTargets";

/**
 * Notifications (Cat 32 — deliver-half) — workspace-level (app-independent) page.
 * Reads the REAL backend GET /api/workspace/notifications?workspace_id=… and lets
 * the recipient mark items read (per-item + mark-all). The recipient is always the
 * server-derived actor — this page never sends an identity. No stubs; honest
 * empty/error states.
 */
export type NotificationKind = "info" | "success" | "warning" | "error";

export interface NotificationItem {
  id: string;
  recipient: string;
  title: string;
  body?: string | null;
  kind: NotificationKind;
  workspace_id?: string | null;
  read_at?: string | null;
  created_at: string;
  is_read: boolean;
  // cat-16 dims (B6: category chip + decision badge; absent on legacy rows)
  category?: string | null;
  topic?: string | null;
  severity?: string | null;
  engagement_id?: string | null;
  requires_decision?: boolean;
  decision_ref?: string | null;
}

export interface NotificationsPageProps {
  apiBase?: string;
  fetchImpl?: typeof fetch;
}

type ViewState =
  | { kind: "loading" }
  | { kind: "error"; message: string }
  | { kind: "ready"; items: NotificationItem[] };

const KIND_TONE: Record<NotificationKind, string> = {
  info: "var(--color-text-secondary)",
  success: "var(--color-success, #2e7d32)",
  warning: "var(--color-warning, #b26a00)",
  error: "var(--color-danger, #c62828)",
};

export function NotificationsPage({
  apiBase = "/api/workspace",
  fetchImpl,
}: NotificationsPageProps): React.ReactElement {
  const { ws } = useParams({ strict: false }) as { ws?: string };
  const navigate = useNavigate();
  const { toast } = useToast();
  const [state, setState] = useState<ViewState>({ kind: "loading" });
  // 4.2 — per-item mark-read recovery: ids whose last mark-read FAILED (button flips to Retry),
  // and ids with a request in flight (blocks duplicate submits). Server state stays honest — we
  // never optimistically flip is_read; the row updates only from the server response.
  const [markFailed, setMarkFailed] = useState<Set<string>>(new Set());
  const [markBusy, setMarkBusy] = useState<Set<string>>(new Set());
  // Synchronous duplicate guard: state updates land a render late, so a same-tick double
  // click would pass a state-only check — the ref refuses re-entry immediately.
  const markBusyRef = useRef<Set<string>>(new Set());
  // cat-16 B6: the Preferences tab is LAZY — the panel (and its fetch) mounts only on selection,
  // so the default list render (pinned by NotificationsPage.spec) is byte-for-byte unchanged.
  const [tab, setTab] = useState<"list" | "prefs">("list");

  const base = apiBase.replace(/\/$/, "");
  const f = fetchImpl ?? fetch;

  // H3.4 (H3.3 D1): the CANONICAL unread count from the dedicated uncapped endpoint — the SAME
  // number the TopNav badge shows. The list below is capped at the server's _MAX_RESULTS, so counting
  // unread among the FETCHED items (as this header used to) silently undercounts and could disagree
  // with the badge. Best-effort: null when the count endpoint is unavailable (e.g. a test that mocks
  // only the list), in which case the header falls back to the visible-list unread.
  const [canonicalUnread, setCanonicalUnread] = useState<number | null>(null);

  const load = useCallback(async () => {
    setState({ kind: "loading" });
    try {
      const qs = ws ? `?workspace_id=${encodeURIComponent(ws)}` : "";
      const res = await f(`${base}/notifications${qs}`, { method: "GET" });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const items = (await res.json()) as NotificationItem[];
      setState({ kind: "ready", items });
      // Canonical unread count — separate, best-effort, never fails the list render.
      try {
        const cRes = await f(`${base}/notifications/unread-count${qs}`, { method: "GET" });
        if (cRes.ok) {
          const c = (await cRes.json()) as { unread_count?: number };
          setCanonicalUnread(typeof c.unread_count === "number" ? c.unread_count : null);
        }
      } catch {
        /* header falls back to the visible-list count */
      }
    } catch (e) {
      setState({ kind: "error", message: e instanceof Error ? e.message : String(e) });
    }
    // base/f are derived from props; ws is the only meaningful dep.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ws]);

  useEffect(() => {
    void load();
  }, [load]);

  const markRead = async (id: string) => {
    if (markBusyRef.current.has(id)) return; // duplicate-submit guard (synchronous)
    markBusyRef.current.add(id);
    setMarkBusy((s) => new Set(s).add(id));
    try {
      const res = await f(`${base}/notifications/${encodeURIComponent(id)}/read`, { method: "POST" });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const updated = (await res.json()) as NotificationItem;
      setState((s) =>
        s.kind === "ready"
          ? { kind: "ready", items: s.items.map((n) => (n.id === id ? updated : n)) }
          : s,
      );
      // A successful (re)try clears the failure marker.
      setMarkFailed((s) => { const next = new Set(s); next.delete(id); return next; });
    } catch (e) {
      // 4.2 — visible recovery, not a silent swallow: canonical danger toast + the item's
      // button flips to Retry. Row state stays honest (never optimistically flipped).
      setMarkFailed((s) => new Set(s).add(id));
      toast(`Couldn't mark as read: ${e instanceof Error ? e.message : "error"} — use Retry`, "danger");
    } finally {
      markBusyRef.current.delete(id);
      setMarkBusy((s) => { const next = new Set(s); next.delete(id); return next; });
    }
  };

  // 4.1 — deep link from fields already in the contract: engagement_id is the one unambiguous
  // internal destination (a scope-node id → node home). Router-level guards/walls decide access
  // — a sealed or missing target renders the existing sealed≡not-found surface, never a leak.
  const deepLinkTarget = (n: NotificationItem) =>
    ws && n.engagement_id ? navTo.nodeHome(ws, n.engagement_id) : null;

  const markAllRead = async () => {
    try {
      const res = await f(`${base}/notifications/read-all`, { method: "POST" });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      await load();
    } catch {
      // ignore; load() on next mount reconciles
    }
  };

  const unreadCount = state.kind === "ready" ? state.items.filter((n) => !n.is_read).length : 0;
  // Header shows the CANONICAL count (matches the badge); the mark-all button stays gated on the
  // visible-list unread. They can differ when the list is capped — that is the honest "matching vs
  // displayed" behaviour (H3.3 rule 4), not a bug.
  const headerUnread = canonicalUnread ?? unreadCount;

  if (tab === "prefs") {
    return (
      <div className="py-1" data-app="notifications">
        <div className="flex items-center gap-3 px-3 pt-2.5 pb-1.5">
          <button
            type="button"
            onClick={() => setTab("list")}
            className="text-[10px] font-semibold uppercase tracking-wide text-[var(--color-text-secondary)] hover:underline"
          >
            Notifications
          </button>
          <span className="text-[10px] font-semibold uppercase tracking-wide text-[var(--color-text-primary)]">
            Preferences
          </span>
        </div>
        <NotificationPrefsPanel fetchImpl={fetchImpl} apiBase={apiBase} />
      </div>
    );
  }

  return (
    <div className="py-1" data-app="notifications">
      <div className="flex items-center justify-between px-3 pt-2.5 pb-1.5">
        <p className="text-[10px] font-semibold uppercase tracking-wide text-[var(--color-text-secondary)]">
          Notifications{headerUnread > 0 ? ` (${headerUnread})` : ""}
        </p>
        <button
          type="button"
          onClick={() => setTab("prefs")}
          className="text-xs text-[var(--color-accent)] hover:underline"
          aria-label="Notification preferences"
        >
          Preferences
        </button>
        {unreadCount > 0 && (
          <button
            type="button"
            onClick={markAllRead}
            className="text-xs text-[var(--color-accent)] hover:underline"
          >
            Mark all read
          </button>
        )}
      </div>

      {state.kind === "loading" && (
        <div role="status" className="px-3 py-2 text-sm text-[var(--color-text-secondary)]">Loading…</div>
      )}
      {state.kind === "error" && (
        <div role="alert" className="px-3 py-2 text-sm text-[var(--color-text-secondary)]">
          Couldn’t load notifications: {state.message}
        </div>
      )}
      {state.kind === "ready" &&
        (state.items.length === 0 ? (
          <div className="px-3 py-2 text-sm text-[var(--color-text-secondary)]">No notifications.</div>
        ) : (
          <ul className="flex flex-col">
            {state.items.map((n) => {
              const target = deepLinkTarget(n);
              // 4.1 — the item body is activatable ONLY when a valid internal destination exists
              // (missing/absent target → plain text, no dead affordance, no crash). Mark-read /
              // Retry are separate controls and never navigate.
              const body = (
                <>
                  <div className="flex items-center gap-2">
                    {!n.is_read && (
                      <span
                        aria-label="unread"
                        className="h-2 w-2 shrink-0 rounded-full bg-[var(--color-accent)]"
                      />
                    )}
                    <span
                      className="text-xs font-medium uppercase tracking-wide"
                      style={{ color: KIND_TONE[n.kind] }}
                    >
                      {n.kind}
                    </span>
                    <span
                      className={
                        n.is_read
                          ? "text-sm text-[var(--color-text-secondary)] truncate"
                          : "text-sm font-medium text-[var(--color-text-primary)] truncate"
                      }
                    >
                      {n.title}
                    </span>
                    {n.category && (
                      <span className="shrink-0 rounded border border-[var(--color-border-subtle)] px-1 text-[10px] text-[var(--color-text-secondary)]">
                        {n.category}
                      </span>
                    )}
                    {n.requires_decision && (
                      <span className="shrink-0 rounded bg-[var(--color-accent)] px-1 text-[10px] font-medium text-white">
                        needs decision
                      </span>
                    )}
                  </div>
                  {n.body && (
                    <p className="mt-0.5 text-xs text-[var(--color-text-secondary)]">{n.body}</p>
                  )}
                </>
              );
              return (
                <li
                  key={n.id}
                  className="flex items-start justify-between gap-3 px-3 py-2 hover:bg-[var(--color-surface-muted)]"
                  data-read={n.is_read}
                >
                  {target ? (
                    <button
                      type="button"
                      data-testid={`notif-open-${n.id}`}
                      aria-label={`Open ${n.title}`}
                      onClick={() => navigate(target as never)}
                      className="min-w-0 flex-1 cursor-pointer text-left"
                    >
                      {body}
                    </button>
                  ) : (
                    <div className="min-w-0 flex-1">{body}</div>
                  )}
                  {!n.is_read && (
                    <button
                      type="button"
                      disabled={markBusy.has(n.id)}
                      onClick={() => markRead(n.id)}
                      className="shrink-0 text-xs text-[var(--color-accent)] hover:underline disabled:opacity-50"
                    >
                      {markFailed.has(n.id) ? "Retry mark read" : "Mark read"}
                    </button>
                  )}
                </li>
              );
            })}
          </ul>
        ))}
    </div>
  );
}
